import { matchTimeDeltaFromDragPx } from '../../core/dubbing/layout.ts';
import {
  clearTimingPreviewMs,
  setTimingPreviewMs,
} from '../../core/dubbing/timingPreview.ts';

export const DUBBING_CUE_CLASS = 'unisubs-dubbing-cue';

const DRAG_THRESHOLD_PX = 3;
/** Fallback when platform never signals settle (Netflix / paused iframe). */
const SEEK_SETTLE_FALLBACK_MS = 1500;
/** Treat currentTime within this window of target as settled. */
const SEEK_SETTLE_NEAR_MS = 150;

export type DubbingTimingDragDeps = {
  isDubbingMode: () => boolean;
  getCurrentTimeMs: () => number;
  getDurationMs: () => number;
  seekMs: (ms: number) => void;
  /** Optional: pause while scrubbing, restore on release. */
  isPaused?: () => boolean;
  pause?: () => void;
  play?: () => void;
  /**
   * Called whenever the paint clock preview changes (or clears).
   * Host should invalidate the subtitle renderer — do not seek here.
   */
  onPreview?: (previewMs: number | null) => void;
  /**
   * After a commit seek, invoke `done` once seek has settled. Return an optional
   * disposer. Drag also polls near `targetMs` and arms a fallback timer.
   */
  whenSeekSettled?: (
    done: () => void,
    targetMs: number,
  ) => (() => void) | void;
};

type DragState = {
  startClientX: number;
  startTimeMs: number;
  localScalePxPerMs: number;
  pointerId: number;
  active: boolean;
  wasPlaying: boolean;
  cue: HTMLElement;
};

type PendingSettle = {
  dispose: () => void;
  wasPlaying: boolean;
};

/** True while a dubbing cue is mid horizontal seek-drag. */
let dragging = false;

export function isDubbingTimingDragging(): boolean {
  return dragging;
}

function clampTimeMs(ms: number, durationMs: number): number {
  if (!Number.isFinite(ms)) return 0;
  if (!(durationMs > 0)) return Math.max(0, ms);
  return Math.min(Math.max(0, ms), durationMs);
}

/**
 * Horizontal drag on a dubbing cue previews match-time on the ticker so the
 * block stays under the pointer (center line = now). Video seek happens once
 * on release — avoids stutter from continuous platform seeks (Netflix / mobile).
 * Does not change delay.
 */
export function installDubbingTimingDrag(deps: DubbingTimingDragDeps): () => void {
  let drag: DragState | null = null;
  let pendingSettle: PendingSettle | null = null;

  const detachWindowListeners = () => {
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', endDrag, true);
    window.removeEventListener('pointercancel', onPointerCancel, true);
  };

  const publishPreview = (ms: number | null) => {
    if (ms === null) clearTimingPreviewMs();
    else setTimingPreviewMs(ms);
    deps.onPreview?.(ms);
  };

  /**
   * Drop a pending post-release settle.
   * - `finish`: clear preview and restore play if the scrub had paused playback.
   * - `inherit`: clear listeners only; return wasPlaying for the next gesture.
   */
  const clearPendingSettle = (mode: 'finish' | 'inherit'): boolean => {
    if (!pendingSettle) return false;
    const { dispose, wasPlaying } = pendingSettle;
    pendingSettle = null;
    dispose();
    if (mode === 'finish') {
      publishPreview(null);
      if (wasPlaying) deps.play?.();
      return false;
    }
    return wasPlaying;
  };

  /** End an in-flight drag without seeking (e.g. left shadowing mid-gesture). */
  const abortDrag = (restorePlay: boolean) => {
    const hadDrag = Boolean(drag);
    const wasPlaying = drag?.wasPlaying ?? false;
    const active = drag?.active ?? false;
    if (drag) {
      const { cue, pointerId } = drag;
      try {
        cue.releasePointerCapture?.(pointerId);
      } catch {
        // ignore
      }
      cue.classList.remove('is-timing-drag');
      drag = null;
    }
    dragging = false;
    detachWindowListeners();

    // Pending settle after a prior release: clear preview and restore play.
    clearPendingSettle('finish');
    if (hadDrag) publishPreview(null);
    if (restorePlay && hadDrag && wasPlaying && active) deps.play?.();
  };

  const previewFromDx = (dx: number, state: DragState): number => {
    const deltaMs = matchTimeDeltaFromDragPx(dx, state.localScalePxPerMs);
    return clampTimeMs(state.startTimeMs + deltaMs, deps.getDurationMs());
  };

  const armSeekSettle = (targetMs: number, wasPlaying: boolean) => {
    // Complete any prior settle (should be rare) so play debt is not dropped.
    clearPendingSettle('finish');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      const settle = pendingSettle;
      pendingSettle = null;
      settle?.dispose();
      publishPreview(null);
      if (wasPlaying) deps.play?.();
    };

    const platformUnsub = deps.whenSeekSettled?.(finish, targetMs);
    const pollId = window.setInterval(() => {
      if (Math.abs(deps.getCurrentTimeMs() - targetMs) <= SEEK_SETTLE_NEAR_MS) {
        finish();
      }
    }, 50);
    const timer = window.setTimeout(finish, SEEK_SETTLE_FALLBACK_MS);

    pendingSettle = {
      wasPlaying,
      dispose: () => {
        // Poison finish so a late seeked/poll/fallback cannot double-play.
        done = true;
        window.clearTimeout(timer);
        window.clearInterval(pollId);
        platformUnsub?.();
      },
    };
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!deps.isDubbingMode()) {
      abortDrag(true);
      return;
    }
    const dx = event.clientX - drag.startClientX;
    if (!drag.active) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      drag.active = true;
      dragging = true;
      drag.cue.classList.add('is-timing-drag');
      if (drag.wasPlaying) deps.pause?.();
    }
    event.preventDefault();
    // Preview only — no seek until release.
    publishPreview(previewFromDx(dx, drag));
  };

  const endDrag = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!deps.isDubbingMode()) {
      abortDrag(true);
      return;
    }
    const { startClientX, cue, active, wasPlaying } = drag;
    const state = drag;
    try {
      cue.releasePointerCapture?.(event.pointerId);
    } catch {
      // ignore
    }
    drag = null;
    dragging = false;
    cue.classList.remove('is-timing-drag');
    detachWindowListeners();

    if (!active) {
      publishPreview(null);
      // Inactive tap after a prior settle inherit: restore if we still owe play.
      if (wasPlaying && deps.isPaused?.()) deps.play?.();
      return;
    }
    event.preventDefault();

    const targetMs = previewFromDx(event.clientX - startClientX, state);
    // Keep paint on targetMs until seek settles to avoid a one-frame snap-back.
    publishPreview(targetMs);
    deps.seekMs(targetMs);
    armSeekSettle(targetMs, wasPlaying);
  };

  const onPointerCancel = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    // Interrupted gesture — do not commit a seek.
    abortDrag(true);
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    if (!deps.isDubbingMode()) return;
    const target = event.target as Element | null;
    if (target?.closest?.('.unisubs-sub-drag-handle')) return;

    const cue = target?.closest?.(`.${DUBBING_CUE_CLASS}`) as HTMLElement | null;
    if (!cue) return;
    if (!cue.closest('#mainSub, #secondSub')) return;

    const start = Number(cue.dataset.start);
    const end = Number(cue.dataset.end);
    const base = cue.querySelector('.unisubs-dubbing-base') as HTMLElement | null;
    const width = base?.offsetWidth || cue.offsetWidth;
    if (!(width > 0) || !Number.isFinite(start) || !Number.isFinite(end)) return;

    event.preventDefault();
    event.stopPropagation();

    // Inherit play debt from an interrupted settle so a quick re-tap cannot leave
    // the video paused forever; stay paused for the new scrub if we still owe play.
    const inheritedPlaying = clearPendingSettle('inherit');
    publishPreview(null);

    drag = {
      startClientX: event.clientX,
      startTimeMs: deps.getCurrentTimeMs(),
      // Dragging a cue block → map with that cue's crossing scale (width/duration).
      localScalePxPerMs: width / Math.max(end - start, 1),
      pointerId: event.pointerId,
      active: false,
      wasPlaying: inheritedPlaying || !(deps.isPaused?.() ?? true),
      cue,
    };
    try {
      cue.setPointerCapture(event.pointerId);
    } catch {
      // happy-dom / older engines
    }
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', endDrag, true);
    window.addEventListener('pointercancel', onPointerCancel, true);
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    // Mode leave / unmount: restore play for in-flight drag or pending settle.
    abortDrag(true);
  };
}
